<template>
  <v-dialog v-model="showModal" max-width="800px" persistent>
    <v-card>
      <v-card-title class="modal-header">
        <span>{{ isEditing ? 'Edit Equipment' : 'Add Equipment' }}</span>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-form ref="formRef">
          <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
            {{ error }}
          </v-alert>

          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="localFormData.equipment_type"
                :items="equipmentTypes"
                label="Equipment Type"
                variant="outlined"
                :rules="[v => !!v || 'Equipment type is required']"
                required
              ></v-select>
            </v-col>
            <v-col v-if="!isEditing" cols="12" sm="6">
              <v-text-field
                v-model.number="localFormData.quantity"
                label="Quantity"
                type="number"
                min="1"
                variant="outlined"
                :rules="[v => !!v || 'Quantity is required']"
                required
              ></v-text-field>
            </v-col>
            <v-col v-else cols="12" sm="6">
              <v-select
                v-model="localFormData.status"
                :items="statusOptions"
                label="Status"
                variant="outlined"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.manufacturer"
                label="Manufacturer"
                variant="outlined"
                placeholder="e.g., Dell, HP, Lenovo"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.model"
                label="Model"
                variant="outlined"
                placeholder="Model name/number"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="localFormData.rating"
                label="Rating (1-10)"
                type="number"
                min="1"
                max="10"
                variant="outlined"
                placeholder="Rate 1-10"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.screen"
                label="Screen"
                variant="outlined"
                placeholder="e.g., 15.6 inch"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.cpu"
                label="CPU"
                variant="outlined"
                placeholder="e.g., Intel i5"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.ram"
                label="RAM"
                variant="outlined"
                placeholder="e.g., 8GB"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.storage"
                label="Storage"
                variant="outlined"
                placeholder="e.g., 256GB SSD"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.gpu"
                label="GPU"
                variant="outlined"
                placeholder="e.g., Intel UHD"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-combobox
                v-model="localFormData.os"
                :items="osOptions"
                label="Operating System"
                variant="outlined"
                placeholder="Select or type OS"
              ></v-combobox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="localFormData.location"
                :items="locationOptions"
                label="Location"
                variant="outlined"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-combobox
                v-model="localFormData.charger"
                :items="chargerOptions"
                label="Charger"
                variant="outlined"
                placeholder="Select or type charger"
                hint="e.g. USB-C 65W, Barrel 90W. Copied when adding more of this model."
                persistent-hint
                clearable
              ></v-combobox>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="localFormData.value"
                label="Value ($)"
                type="number"
                min="0"
                step="0.01"
                variant="outlined"
                prefix="$"
                placeholder="e.g., 250.00"
                hint="Used on donation receipts. Copied when adding more of this model."
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <div class="section-label mb-3">Donation Info</div>

          <v-row>
            <v-col cols="12" sm="6">
              <v-combobox
                v-model="localFormData.donor"
                :items="donorOptions"
                label="Donor"
                variant="outlined"
                placeholder="Person or organization"
                hint="Not copied when adding more of this model unless you specify it"
                persistent-hint
                clearable
              ></v-combobox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localFormData.donated_on"
                label="Donation Date"
                type="date"
                variant="outlined"
                hint="Printed on the donation receipt"
                persistent-hint
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-row v-if="isEditing && localFormData.asset_tag">
            <v-col cols="12">
              <v-text-field
                v-model="localFormData.asset_tag"
                label="Asset Tag"
                variant="outlined"
                readonly
                hint="Asset tag is auto-generated"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="localFormData.comments"
                label="Comments"
                variant="outlined"
                rows="3"
                placeholder="Additional notes..."
              ></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" @click="closeModal" :disabled="submitting">
          Cancel
        </v-btn>
        <v-btn color="primary" @click="submitForm" :loading="submitting">
          {{ isEditing ? 'Update' : 'Add' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { watch, ref, computed, onMounted } from 'vue'
import { api } from '@/composables/api'

interface FormData {
  equipment_type: string
  quantity: number
  manufacturer: string
  model: string
  rating: number | null
  screen: string
  cpu: string
  ram: string
  storage: string
  gpu: string
  os: string
  charger: string
  status: string
  location: string
  asset_tag: string
  comments: string
  value: number | null
  donor: string
  donated_on: string
}

interface School {
  id: string
  _id?: string
  name: string
}

interface Props {
  show: boolean
  isEditing: boolean
  formData: FormData
  submitting: boolean
  error: string
}

const props = defineProps<Props>()
const emit = defineEmits(['close', 'submit'])

const formRef = ref<any>(null)
const localFormData = ref<FormData>({ ...props.formData })
const schools = ref<School[]>([])
const donorOptions = ref<string[]>([])
const chargerOptions = ref<string[]>([])

const showModal = computed({
  get: () => props.show,
  set: (value) => {
    if (!value) emit('close')
  }
})

const equipmentTypes = [
  { title: 'Laptop', value: 'laptop' },
  { title: 'Desktop', value: 'desktop' },
  { title: 'Monitor', value: 'monitor' },
  { title: 'Tablet', value: 'tablet' },
  { title: 'Other', value: 'other' }
]

const statusOptions = [
  { title: 'Processing', value: 'processing' },
  { title: 'Troubleshooting', value: 'troubleshooting' },
  { title: 'Parts', value: 'parts' },
  { title: 'Available', value: 'available' },
  { title: 'Claimed', value: 'claimed' },
  { title: 'Delivered', value: 'delivered' },
  { title: 'Retired', value: 'retired' }
]

const osOptions = [
  'Windows',
  'macOS',
  'Chrome OS',
  'Linux',
  'Linux (Mint)',
  'Linux (Zorin)',
  'Linux (Ubuntu)'
]

const locationOptions = computed(() => {
  const options = [{ title: 'SCS', value: 'SCS' }]

  // Add schools to location options
  schools.value.forEach(school => {
    options.push({
      title: school.name,
      value: school.name
    })
  })

  return options
})

const loadSchools = async () => {
  try {
    const response = await api.get('/schools')
    schools.value = response.data || response || []
  } catch (error) {
    console.error('Error loading schools:', error)
    schools.value = []
  }
}

const loadDonors = async () => {
  try {
    const response = await api.get('/equipment/donors/list')
    const donors = response.data || response || []
    donorOptions.value = donors.map((d: any) => d.donor).filter(Boolean)
  } catch (error) {
    console.error('Error loading donors:', error)
    donorOptions.value = []
  }
}

const loadChargers = async () => {
  try {
    const response = await api.get('/equipment/chargers/list')
    const chargers = response.data || response || []
    chargerOptions.value = chargers.map((c: any) => c.charger).filter(Boolean)
  } catch (error) {
    console.error('Error loading chargers:', error)
    chargerOptions.value = []
  }
}

const closeModal = () => {
  emit('close')
}

const submitForm = async () => {
  const { valid } = await formRef.value.validate()

  if (valid) {
    emit('submit', localFormData.value)
  }
}

watch(() => props.formData, (newVal) => {
  localFormData.value = { ...newVal }
}, { deep: true })

onMounted(() => {
  loadSchools()
  loadDonors()
  loadChargers()
})
</script>

<style scoped>
.section-label {
  font-weight: 600;
  color: var(--primary-color, #2c4a6b);
  font-size: 0.95rem;
}

.modal-header {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: white;
}
</style>
